import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ChevronLeft, Mail, KeyRound, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [step, setStep] = useState('email'); // email | code | done
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Error');
      }
      toast.success('Código enviado a tu correo');
      setStep('code');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (newPassword.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      setStep('done');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center px-4 py-3 border-b border-gray-100">
        <button
          onClick={() => step === 'code' ? setStep('email') : navigate('/login')}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12">
        <div className="w-full max-w-sm mx-auto">
          <Link to="/" className="block text-center mb-8">
            <img src="/utentik-logo.png" alt="utentik" className="h-7 mx-auto" />
          </Link>

          {/* Step 1: Email */}
          {step === 'email' && (
            <>
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-7 h-7 text-primary" />
              </div>
              <h1 className="font-rubik text-2xl font-bold text-center text-gray-500 mb-2">
                Recuperar contraseña
              </h1>
              <p className="text-center text-gray-400 mb-8">
                Ingresa tu correo y te enviaremos un código de verificación
              </p>

              {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 text-sm mb-6" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSendCode} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-gray-500">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="mt-2 input-airbnb"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary-hover rounded-xl py-6 font-semibold"
                  data-testid="forgot-send-btn"
                >
                  {loading ? 'Enviando...' : 'Enviar código'}
                </Button>
              </form>

              <p className="text-center text-gray-400 mt-8">
                <Link to="/login" className="text-primary font-medium" data-testid="back-to-login">
                  Volver al inicio de sesión
                </Link>
              </p>
            </>
          )}

          {/* Step 2: Code + New Password */}
          {step === 'code' && (
            <>
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <KeyRound className="w-7 h-7 text-primary" />
              </div>
              <h1 className="font-rubik text-2xl font-bold text-center text-gray-500 mb-2">
                Ingresa el código
              </h1>
              <p className="text-center text-gray-400 mb-8">
                Enviamos un código a <span className="font-medium text-gray-500">{email}</span>
              </p>

              {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 text-sm mb-6" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-5">
                <div>
                  <Label htmlFor="code" className="text-sm font-medium text-gray-500">Código de verificación</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="text"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="mt-2 input-airbnb text-center text-2xl tracking-[0.5em] font-mono"
                    required
                    data-testid="reset-code-input"
                  />
                </div>
                <div>
                  <Label htmlFor="new-password" className="text-sm font-medium text-gray-500">Nueva contraseña</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min. 6 caracteres"
                    className="mt-2 input-airbnb"
                    required
                    minLength={8}
                    data-testid="reset-new-password"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password" className="text-sm font-medium text-gray-500">Confirmar contraseña</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repite la contraseña"
                    className="mt-2 input-airbnb"
                    required
                    minLength={8}
                    data-testid="reset-confirm-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || code.length < 6}
                  className="w-full bg-primary hover:bg-primary-hover rounded-xl py-6 font-semibold"
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Actualizando...' : 'Cambiar contraseña'}
                </Button>
              </form>

              <button
                onClick={handleSendCode}
                className="block mx-auto mt-6 text-sm text-primary font-medium"
                data-testid="resend-code-btn"
              >
                Reenviar código
              </button>
            </>
          )}

          {/* Step 3: Success */}
          {step === 'done' && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="font-rubik text-2xl font-bold text-gray-500 mb-2">
                Contraseña actualizada
              </h1>
              <p className="text-gray-400 mb-8">
                Ya puedes iniciar sesión con tu nueva contraseña
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-primary hover:bg-primary-hover rounded-xl py-6 font-semibold"
                data-testid="go-to-login-btn"
              >
                Ir a iniciar sesión
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
